import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import * as adminApi from '../../Services/adminApi';

const ManageCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null); // null for new, object for edit
    const [categoryName, setCategoryName] = useState('');

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminApi.getCategories();
            setCategories(data);
            setError('');
        } catch (err) {
            setError('Failed to fetch categories.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentCategory(null);
        setCategoryName('');
    };

    const handleShowModal = (category = null) => {
        setCurrentCategory(category);
        setCategoryName(category ? category.name : '');
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!categoryName.trim()) {
            alert('Category name cannot be empty.');
            return;
        }

        try {
            if (currentCategory) {
                // Update existing category
                await adminApi.updateCategory(currentCategory.id, { name: categoryName });
            } else {
                // Create new category
                await adminApi.createCategory({ name: categoryName });
            }
            fetchCategories();
            handleCloseModal();
        } catch (err) {
            alert('Failed to save category.');
            console.error(err);
        }
    };

    const handleDelete = async (categoryId, name) => {
        if (window.confirm(`Are you sure you want to delete the category "${name}"? This may affect existing listings.`)) {
            try {
                await adminApi.deleteCategory(categoryId);
                fetchCategories();
            } catch (err) {
                alert('Failed to delete category.');
            }
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center">
                <h2>Category Management</h2>
                <Button variant="primary" onClick={() => handleShowModal()}>
                    <i className="fas fa-plus me-2"></i> Create New
                </Button>
            </div>
            <hr />

            {loading && <div className="text-center"><Spinner animation="border" /></div>}
            {error && <Alert variant="danger">{error}</Alert>}

            {!loading && !error && (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(cat => (
                            <tr key={cat.id}>
                                <td>{cat.id}</td>
                                <td>{cat.name}</td>
                                <td className="text-end">
                                    <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowModal(cat)}>
                                        <i className="fas fa-edit"></i> Edit
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(cat.id, cat.name)}>
                                        <i className="fas fa-trash"></i> Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentCategory ? 'Edit Category' : 'Create New Category'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group controlId="categoryName">
                            <Form.Label>Category Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter category name"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                autoFocus
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ManageCategories;